import React, { useCallback, useContext } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import {
  NumberComponent,
  NumberFormat,
  availableFormats,
  componentTemplateOptions,
  iconForNumberFormat,
  titleForNumberFormat,
} from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { MBSingleSelect } from '@shared/scenes/workflows/views/components/singleSelect';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBWorkflowDefaultComponentOptions } from './defaultOptions';

const cursorPointerStyle = { cursor: 'pointer' } as StyleProp<ViewStyle>;

interface Props {
  component: NumberComponent;
}

export function MBWorkflowNumberOptions({ component }: Props) {
  const colors = useContext(ColorThemeContext);
  const dispatch = useWorkflowDispatch();

  const setFormat = useCallback(
    (format: NumberFormat) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          icon: iconForNumberFormat(format),
          format,
        },
      });
    },
    [component, dispatch],
  );

  const renderSubtypeMenu = componentTemplateOptions.isSubtypeEditable(component);

  return (
    <MBWorkflowDefaultComponentOptions
      component={component}
      extraSections={({ closeSubmenus }) =>
        renderSubtypeMenu && (
          <>
            <View paddingHorizontal={3} paddingVertical={1}>
              <MBTextOverlineBold color={colors.foregroundInactive}>
                {i18n('workflowTemplate.format')}
              </MBTextOverlineBold>
            </View>
            <RCSubMenu
              key="format"
              style={{ zIndex: 999, padding: 0 }}
              title={
                <MBView
                  row
                  centerVertically
                  paddingVertical={1}
                  paddingHorizontal={3}
                  style={cursorPointerStyle}
                >
                  <MBIconV2
                    name={iconForNumberFormat(component.format)}
                    size={grid(3)}
                    color={colors.foregroundActive}
                  />
                  <View paddingLeft={1} fill centerVertically>
                    <MBTextBody1>{titleForNumberFormat(component.format)}</MBTextBody1>
                  </View>
                  <MBIconV2
                    name="arrow-point-right"
                    color={colors.foregroundActive}
                    size={grid(3)}
                  />
                </MBView>
              }
            >
              <View>
                <MBSingleSelect
                  title={i18n('workflowTemplate.format')}
                  options={availableFormats}
                  getIcon={iconForNumberFormat}
                  getLabel={titleForNumberFormat}
                  onSelectOption={format => {
                    setFormat(format as NumberFormat);
                    closeSubmenus();
                  }}
                  selectedOption={component.format}
                />
                <View padding={1} />
              </View>
            </RCSubMenu>
          </>
        )
      }
    />
  );
}
